import { Base } from '../../abstract/base.model';
import { DroneStatus } from './drone-status.model';
import { ZohoSyncStatus } from './zoho-sync-status.model';
import { isRecord } from '../utils/is-record';
import { dateOrNull, nullableString } from '../utils/wire-value-utils';

/**
 * An ADN inventory drone, built from the hub's `/admin/drones` JSON (hub `data-models/drone-inventory.ts`).
 * Field names match the hub's (`nNumber`, not the web portal's `registrationNumber`).
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`), so a form built from a drone can't blank a redacted value. Display fallbacks belong in templates.
 * Only the four read-only Zoho FSM sync fields the drone pages show are mapped (Doug, ADN-2471); the rest of the
 * Zoho fields (asset id, Zoho Desk) are not, as the Zoho integration is slated for removal.
 */
export class Drone extends Base {
  // Writable via PUT /admin/drones/:id (the hub's DRONE_WRITABLE)
  name?: string | null;
  manufacturerId?: string | null;
  modelId?: string | null;
  serialNumber?: string | null;
  packagingSerialNumber?: string | null;
  version?: string | null;
  inventoryNote?: string | null;
  nNumber?: string | null;
  /** ISO date as sent by the hub, so it round-trips unchanged. */
  nNumberActiveDate?: string | null;
  remoteId?: string | null;
  remoteBroadcastModule?: string | null;

  /** Writable only through PUT /admin/drones/:id/status (and on create). */
  status?: DroneStatus | null;

  // Read-only, set by the hub
  assignedOrganizationId?: string | null;
  manufacturerName?: string | null;
  modelName?: string | null;

  // Read-only Zoho FSM sync state, set by the hub's Zoho sync
  zohoFsmProductId?: string | null;
  zohoFsmSyncStatus?: ZohoSyncStatus | null;
  zohoFsmSyncedAt: Date | null;
  zohoFsmSyncError?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.name = nullableString(source['name']);
    this.manufacturerId = nullableString(source['manufacturerId']);
    this.modelId = nullableString(source['modelId']);
    this.serialNumber = nullableString(source['serialNumber']);
    this.packagingSerialNumber = nullableString(source['packagingSerialNumber']);
    this.version = nullableString(source['version']);
    this.inventoryNote = nullableString(source['inventoryNote']);
    this.nNumber = nullableString(source['nNumber']);
    this.nNumberActiveDate = nullableString(source['nNumberActiveDate']);
    this.remoteId = nullableString(source['remoteId']);
    this.remoteBroadcastModule = nullableString(source['remoteBroadcastModule']);
    this.status = nullableString(source['status']) as DroneStatus | null | undefined;
    this.assignedOrganizationId = nullableString(source['assignedOrganizationId']);
    this.manufacturerName = nullableString(source['manufacturerName']);
    this.modelName = nullableString(source['modelName']);
    this.zohoFsmProductId = nullableString(source['zohoFsmProductId']);
    this.zohoFsmSyncStatus = nullableString(source['zohoFsmSyncStatus']) as ZohoSyncStatus | null | undefined;
    this.zohoFsmSyncedAt = dateOrNull(source['zohoFsmSyncedAt']);
    this.zohoFsmSyncError = nullableString(source['zohoFsmSyncError']);
  }
}
